import { Button } from '@web/components/ui/button';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@web/components/ui/form';
import { UseFormReturn } from 'react-hook-form';

import { CollectPaymentForm as CollectPaymentFormType } from '@shared/types/collect-payment-form';
import { toast } from 'sonner';
import { useState } from 'react';
import { Spinner } from '@web/components/ui/spinner';

import { Separator } from '@web/components/ui/separator';
import { Label } from '@web/components/ui/label';

import { CardDetails } from '@web/components/ui/card-details';
import { InputMoney } from '@web/components/ui/input-money';
import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';

export function CollectPaymentForm({
  form,
  onSubmit,
}: {
  form: UseFormReturn<CollectPaymentFormType>;
  onSubmit?: (data: CollectPaymentFormType) => Promise<void>;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: CollectPaymentFormType) => {
    setIsSubmitting(true);
    await onSubmit?.(data)
      .then(() => {
        toast.success('Payment successful');
      })
      .catch(() => {
        toast.error('Payment failed');
      });
    setIsSubmitting(false);
  };

  const handleCardDetailsChange = ({
    number,
    exp,
    cvv,
  }: {
    number?: string;
    cvv?: string;
    exp?: string;
  }) => {
    if (number) form.setValue('card.number', number);
    if (exp) form.setValue('card.exp', exp);
    if (cvv) form.setValue('card.cvv', cvv);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="mt-6 grid grid-cols-6 gap-2 gap-y-6"
      >
        <Label className="col-span-full font-bold">Card Details</Label>

        <CardDetails
          className="col-span-6"
          onChange={handleCardDetailsChange}
        />

        <Separator className="col-span-6 my-4" />

        <Label className="col-span-full font-bold">Billing Information</Label>

        <FormField
          control={form.control}
          name="billing.firstName"
          render={({ field }) => (
            <FormItem className="col-span-3">
              <InputAnimatedLabel label="First Name" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.lastName"
          render={({ field }) => (
            <FormItem className="col-span-3">
              <InputAnimatedLabel label="Last Name" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.address1"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <InputAnimatedLabel label="Address" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.zip"
          render={({ field }) => (
            <FormItem className="col-span-2">
              <InputAnimatedLabel label="ZIP" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.email"
          render={({ field }) => (
            <FormItem className="col-span-4">
              <InputAnimatedLabel label="Email" {...field} />
            </FormItem>
          )}
        />

        <Separator className="col-span-6 my-4" />
        <Label className="col-span-full font-bold">Payment</Label>

        <FormField
          control={form.control}
          name="payment.amount"
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormControl>
                <InputMoney value={field.value} onChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="-col-start-2 self-end"
        >
          {isSubmitting ? <Spinner /> : 'Submit'}
        </Button>
      </form>
    </Form>
  );
}
